import { apiClient } from './api-client';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export type NoticeType = 'BREACH_NOTICE' | 'ERASURE_NOTICE' | 'INFORMATIONAL_NOTICE';
export type SourceType = 'NOTICE' | 'CONSENT_NOTICE';
export type NoticeStatus = 'ACTIVE' | 'ARCHIVED';

export interface NoticeFilters {
    noticeType?: NoticeType | 'CONSENT_NOTICE' | 'ALL';
    isAcknowledged?: boolean;
    showAcknowledged?: boolean;
}

export interface NoticeListItem {
    // Source type discriminator
    sourceType?: SourceType;

    // Other Notice fields (when sourceType='NOTICE')
    NoticeId?: number;
    NoticeType?: NoticeType;
    Title?: string;
    Description?: string | null;
    Status?: NoticeStatus;

    // Consent Notice fields (when sourceType='CONSENT_NOTICE')
    CollectionPointId?: number;
    Name?: string; // Collection point name (instead of Title)
    CollectionPointType?: string;
    IsActive?: boolean;
    IsPublished?: boolean;

    // Common fields
    ClientId: number;
    CreatedDate: string;
    CreatedBy: number;
    CreatedByName?: string;

    // Assignment info
    AssignmentId?: number;
    DPId?: number;
    IsAcknowledged?: boolean;
    AcknowledgedDate?: string | null;
    AcknowledgedLanguageCode?: string | null;
    EffectiveDate?: string;
    EmailSent?: boolean;
    SmsSent?: boolean;
    AssignmentStatus?: NoticeStatus;
    AssignedDate?: string;
    AssignedBy?: number;
    AssignedByName?: string;
    ContentJSONSnapshot?: string | null; // For Other Notices
    SnapshotDate?: string | null;
}

export interface NoticeDetails {
    assignmentId: number;
    sourceType: SourceType;
    noticeType?: NoticeType;
    title?: string;
    name?: string; // For Consent Notices
    description?: string | null;
    content?: any; // Parsed ContentJSONSnapshot or SnapshotJSON
    isAcknowledged: boolean;
    acknowledgedDate: string | null;
    acknowledgedLanguageCode: string | null;
    effectiveDate: string;
    assignedDate: string;
    noticeId?: number;
    collectionPointId?: number;
    /** Data Principal is a minor (viewer may be guardian). */
    isMinor: boolean;
    /** Trimmed ExternalIdentifier when minor; null when absent. */
    minorDisplayName: string | null;
}

export interface NoticesSummary {
    total: number;
    unacknowledged: number;
    acknowledged: number;
    byType: {
        consent: number;
        breach: number;
        erasure: number;
        informational: number;
    };
    openByType?: {
        consent: number;      // Assigned but consent not submitted
        breach: number;       // Unacknowledged
        erasure: number;      // Unacknowledged
        informational: number; // Unacknowledged
    };
}

export interface NoticesListApiResponse {
    success: boolean;
    data: NoticeListItem[];
    error?: string;
}

export interface NoticeDetailsApiResponse {
    success: boolean;
    data: NoticeDetails;
    error?: string;
}

export interface NoticesSummaryApiResponse {
    success: boolean;
    data: NoticesSummary;
    error?: string;
}

export interface AcknowledgeNoticeRequest {
    languageCode: string;
}

export interface AcknowledgeNoticeResponse {
    success: boolean;
    data: any;
    message?: string;
    error?: string;
}

// ============================================================================
// NOTICES SERVICE
// ============================================================================

class NoticesService {
    private baseUrl: string;

    constructor() {
        this.baseUrl = '/portal/notices';
    }

    /**
     * Get all notices for the authenticated DP
     */
    async getNotices(filters: NoticeFilters = {}): Promise<NoticeListItem[]> {
        try {
            const params = new URLSearchParams();
            
            if (filters.noticeType && filters.noticeType !== 'ALL') {
                params.append('noticeType', filters.noticeType);
            }
            
            if (filters.isAcknowledged !== undefined) {
                params.append('isAcknowledged', filters.isAcknowledged.toString());
            }
            
            if (filters.showAcknowledged !== undefined) {
                params.append('showAcknowledged', filters.showAcknowledged.toString());
            }

            const url = `${this.baseUrl}${params.toString() ? `?${params.toString()}` : ''}`;
            const response = await apiClient.get<NoticesListApiResponse>(url);
            
            if (!response.success) {
                throw new Error(response.error || 'Failed to fetch notices');
            }

            return response.data;
        } catch (error: any) {
            console.error('Error fetching notices:', error);
            throw new Error(error.message || 'Failed to fetch notices');
        }
    }

    /**
     * Get notice details for a specific assignment
     */
    async getNoticeDetails(assignmentId: number): Promise<NoticeDetails> {
        try {
            const response = await apiClient.get<NoticeDetailsApiResponse>(
                `${this.baseUrl}/${assignmentId}`
            );
            
            if (!response.success) {
                throw new Error(response.error || 'Failed to fetch notice details');
            }

            return response.data;
        } catch (error: any) {
            console.error('Error fetching notice details:', error);
            throw new Error(error.message || 'Failed to fetch notice details');
        }
    }

    /**
     * Submit consent for a NOTICE-type collection point
     * @param collectionPointId - The collection point ID
     * @param dataElements - Map of data element ID to value
     * @param purposeConsents - Map of purpose ID to consent boolean
     * @param languageCode - Language code for the submission
     */
    async submitNoticeConsent(
        collectionPointId: number,
        dataElements: Record<number, string>,
        purposeConsents: Record<number, boolean>,
        languageCode: string = 'en'
    ): Promise<{ consentHeaderId: number; dataPrincipalId: number }> {
        try {
            // Use a longer timeout for consent submission (30 seconds) and disable retries
            // to prevent duplicate submissions (POST requests that create data should not be retried)
            const response = await apiClient.post<{
                success: boolean;
                data: { consentHeaderId: number; dataPrincipalId: number };
                error?: string;
            }>(`/portal/collection-points/${collectionPointId}/consent`, {
                dataElements,
                purposeConsents,
                languageCode
            }, {
                timeout: 30000, // 30 seconds timeout for consent submission
                _skipRetry: true, // Disable retries to prevent duplicate submissions
            } as any);

            if (!response.success) {
                throw new Error(response.error || 'Failed to submit consent');
            }

            return response.data;
        } catch (error: any) {
            console.error('Error submitting notice consent:', error);
            throw new Error(error.message || 'Failed to submit consent');
        }
    }

    /**
     * Acknowledge a notice
     */
    async acknowledgeNotice(assignmentId: number, languageCode: string): Promise<any> {
        try {
            const request: AcknowledgeNoticeRequest = { languageCode };
            const response = await apiClient.post<AcknowledgeNoticeResponse>(
                `${this.baseUrl}/${assignmentId}/acknowledge`,
                request
            );
            
            if (!response.success) {
                throw new Error(response.error || 'Failed to acknowledge notice');
            }

            return response.data;
        } catch (error: any) {
            console.error('Error acknowledging notice:', error);
            throw new Error(error.message || 'Failed to acknowledge notice');
        }
    }

    /**
     * Get notices summary statistics
     */
    async getNoticesSummary(): Promise<NoticesSummary> {
        try {
            const response = await apiClient.get<NoticesSummaryApiResponse>(
                `${this.baseUrl}/summary`
            );
            
            if (!response.success) {
                throw new Error(response.error || 'Failed to fetch notices summary');
            }

            return response.data;
        } catch (error: any) {
            console.error('Error fetching notices summary:', error);
            throw new Error(error.message || 'Failed to fetch notices summary');
        }
    }

    /**
     * Get snapshot JSON for a collection point (for consent notices)
     */
    async getCollectionPointSnapshot(collectionPointId: number): Promise<any> {
        console.log('NoticesService - getCollectionPointSnapshot called', { collectionPointId });
        const url = `/portal/collection-points/${collectionPointId}/snapshot`;
        console.log('NoticesService - Requesting URL:', url);
        
        try {
            const response = await apiClient.get<{ success: boolean; data: any; error?: string }>(url);
            
            console.log('NoticesService - getCollectionPointSnapshot response:', response);
            
            if (!response.success) {
                console.error('NoticesService - getCollectionPointSnapshot failed:', response.error);
                throw new Error(response.error || 'Failed to fetch collection point snapshot');
            }

            return response.data;
        } catch (error: any) {
            console.error('=== NoticesService - Error fetching collection point snapshot ===');
            console.error('CollectionPointId:', collectionPointId);
            console.error('URL:', url);
            console.error('Error:', error);
            console.error('Error response:', error?.response);
            console.error('Error status:', error?.response?.status);
            console.error('Error data:', error?.response?.data);
            console.error('===============================================================');
            throw new Error(error.message || 'Failed to fetch collection point snapshot');
        }
    }

    /**
     * Get notice configuration JSON for a collection point
     */
    async getCollectionPointNoticeConfig(collectionPointId: number): Promise<any | null> {
        console.log('NoticesService - getCollectionPointNoticeConfig called', { collectionPointId });
        const url = `/portal/collection-points/${collectionPointId}/notice-config`;
        console.log('NoticesService - Requesting URL:', url);
        
        try {
            const response = await apiClient.get<{ success: boolean; data: any | null; error?: string }>(url);
            
            console.log('NoticesService - getCollectionPointNoticeConfig response:', response);
            
            if (!response.success) {
                console.error('NoticesService - getCollectionPointNoticeConfig failed:', response.error);
                throw new Error(response.error || 'Failed to fetch notice config');
            }

            return response.data;
        } catch (error: any) {
            console.error('=== NoticesService - Error fetching notice config ===');
            console.error('CollectionPointId:', collectionPointId);
            console.error('URL:', url);
            console.error('Error:', error);
            console.error('Error response:', error?.response);
            console.error('Error status:', error?.response?.status);
            console.error('Error data:', error?.response?.data);
            console.error('=====================================================');
            throw new Error(error.message || 'Failed to fetch notice config');
        }
    }

    /**
     * Verify OTP and create consent for consent notice assignment
     * @param assignmentId - The assignment ID
     * @param otp - 6-digit OTP shared by DP/guardian
     * @returns Consent creation result
     */
    async verifyOtp(assignmentId: number, otp: string): Promise<{
        consentHeaderId: number;
        dataPrincipalId: number;
        assignmentId: number;
    }> {
        try {
            const response = await apiClient.post<{
                success: boolean;
                message?: string;
                data?: {
                    consentHeaderId: number;
                    dataPrincipalId: number;
                    assignmentId: number;
                };
                error?: string;
            }>(`${this.baseUrl}/assignments/${assignmentId}/verify-otp`, {
                otp
            });

            if (!response.success) {
                throw new Error(response.error || 'Failed to verify OTP');
            }

            if (!response.data) {
                throw new Error('No data returned from OTP verification');
            }

            return response.data;
        } catch (error: any) {
            console.error('Error verifying OTP:', error);
            throw new Error(error.message || 'Failed to verify OTP');
        }
    }

    /**
     * Resend OTP for consent notice assignment
     * @param assignmentId - The assignment ID
     */
    async resendOtp(assignmentId: number): Promise<void> {
        try {
            const response = await apiClient.post<{
                success: boolean;
                message?: string;
                error?: string;
            }>(`${this.baseUrl}/assignments/${assignmentId}/resend-otp`);

            if (!response.success) {
                throw new Error(response.error || 'Failed to resend OTP');
            }
        } catch (error: any) {
            console.error('Error resending OTP:', error);
            throw new Error(error.message || 'Failed to resend OTP');
        }
    }
}

// Create and export a singleton instance
export const noticesService = new NoticesService();

// Export default
export default noticesService;

